import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { useAddressAutocompleteActions } from "@redotech/redo-merchant-app-common/hooks/use-address-autocomplete-actions";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import {
  AddressAutocompleteFields,
  AddressAutocompleteFormProps,
  AddressAutocompleteForm as WebAddressForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { AutoCompleteInput } from "@redotech/redo-web/autocomplete-input";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export const AddressForm = memo(function AddressForm({
  form,
  title = "Recipient information",
  hideEmailField = false,
  hideCompanyField = false,
  includeNameField = true,
  autoCompleteOnName = true,
  requirePhone = true,
}: AddressAutocompleteFormProps) {
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const getAddressSuggestionsFromName = useHandler(
    async (nameSearchString: string) => {
      const { suggestions } =
        await outboundLabelsRpcClient.getAddressesFromName({
          nameSearchString,
        });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion,
        displayText: suggestion.name,
      }));
    },
  );

  const getAddressFromNameSuggestion = useHandler(
    async (destinationAddress: FulfillmentOrderAddress) => {
      return {
        name: destinationAddress.name ?? "",
        address1: destinationAddress.address1 ?? "",
        address2: destinationAddress.address2 ?? "",
        city: destinationAddress.city ?? "",
        province: destinationAddress.province ?? "",
        zip: destinationAddress.zip ?? "",
        countryCode: destinationAddress.countryCode as RedoShopifyCountryCode,
        email: destinationAddress.email,
        company: destinationAddress.company,
        phone: destinationAddress.phone,
      };
    },
  );

  const actions = useAddressAutocompleteActions({
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
  });

  return (
    <WebAddressForm
      actions={actions}
      autoCompleteOnName={autoCompleteOnName}
      form={form}
      hideCompanyField={hideCompanyField}
      hideEmailField={hideEmailField}
      includeNameField={includeNameField}
      requirePhone={requirePhone}
      title={title}
    />
  );
});

export interface AddressLineAutocompleteInputProps {
  countryCode: string;
  disabled?: boolean;
  label: string;
  placeholder: string;
  setAddressFields(address: AddressAutocompleteFields): void;
  setValue(value: string): void;
  value: string;
}

export const AddressLineAutocompleteInput = memo(
  function AddressLineAutocompleteInput({
    countryCode,
    disabled = false,
    label,
    placeholder,
    setAddressFields,
    setValue,
    value,
  }: AddressLineAutocompleteInputProps) {
    const actions = useAddressAutocompleteActions();

    const getAddressSuggestionsFromAddressLine = useHandler(
      async (query: string, sessionToken?: string) => {
        return actions.getAddressSuggestionsFromAddressLine(
          query,
          sessionToken,
          countryCode,
        );
      },
    );

    if (disabled) {
      return (
        <RedoTextInput
          label={label}
          placeholder={placeholder}
          setValue={setValue}
          state="disabled"
          value={value}
        />
      );
    }

    return (
      <AutoCompleteInput<AddressAutocompleteFields, string>
        getSuggestions={getAddressSuggestionsFromAddressLine}
        label={label}
        pickSuggestion={actions.getAddressFromAddressLineSuggestion}
        placeholder={placeholder}
        setAutocompleteFields={setAddressFields}
        setValue={setValue}
        value={value}
      />
    );
  },
);
